import { useRef, useState, useEffect, useCallback } from 'react';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function ServicesSlider({ onCardHover }: { onCardHover?: (hovered: boolean) => void }) {
  const services = [
    {
      num: '01',
      title: 'Software and App Development',
      description: 'Build what your business needs, delivered across platforms. From wireframe to production, on a codebase your team can own.',
      image: 'https://readdy.ai/api/search-image?query=Realistic%20photo%20of%20a%20modern%20software%20development%20team%20working%20in%20a%20bright%20naturally%20lit%20open%20office%2C%20multiple%20large%20monitors%20showing%20actual%20code%20and%20design%20wireframes%2C%20developers%20collaborating%20at%20a%20standing%20desk%2C%20coffee%20cups%20and%20sticky%20notes%20scattered%20around%2C%20warm%20afternoon%20sunlight%20streaming%20through%20large%20windows%2C%20authentic%20productive%20workplace%20atmosphere%2C%20candid%20captured%20moment%2C%20shallow%20depth%20of%20field%2C%20lifestyle%20editorial%20photography%2C%20warm%20neutral%20tones%20with%20subtle%20teal%20accents&width=800&height=600&seq=service-01-v2&orientation=landscape&nocache=true',
    },
    {
      num: '02',
      title: 'AI Consulting and Development',
      description: 'Pinpoint where AI moves the needle for your business. Design, integrate and deploy.',
      image: 'https://readdy.ai/api/search-image?query=Realistic%20photo%20of%20two%20business%20professionals%20collaborating%20in%20a%20bright%20modern%20meeting%20room%2C%20an%20Asian%20male%20consultant%20on%20the%20left%20and%20a%20female%20colleague%20on%20the%20right%2C%20both%20seated%20at%20a%20clean%20desk%20with%20hands%20resting%20on%20their%20own%20laptops%20and%20keyboards%2C%20no%20physical%20contact%20between%20them%2C%20a%20large%20wall%20mounted%20monitor%20between%20them%20displaying%20colorful%20data%20analytics%20dashboards%20and%20AI%20model%20visualizations%2C%20the%20Asian%20man%20pointing%20with%20one%20hand%20toward%20key%20metrics%20on%20the%20shared%20screen%2C%20a%20clean%20whiteboard%20filled%20with%20architectural%20diagrams%20on%20the%20wall%20behind%20them%2C%20natural%20daylight%20streaming%20through%20floor%20to%20ceiling%20windows%2C%20potted%20plants%20and%20contemporary%20minimalist%20office%20furniture%2C%20authentic%20corporate%20AI%20consulting%20atmosphere%2C%20candid%20documentary%20style%20photography%2C%20warm%20balanced%20professional%20lighting%2C%20genuine%20intellectual%20engagement%2C%20clear%20personal%20space%20between%20the%20two%20people&width=800&height=600&seq=service-02-v4&orientation=landscape&nocache=true',
    },
    {
      num: '03',
      title: 'Data Engineering and Platforms',
      description: 'Hidden intelligence in your data, surfaced. We turn raw data into valuable insights your systems can leverage.',
      image: 'https://readdy.ai/api/search-image?query=Realistic%20photo%20of%20a%20modern%20data%20center%20server%20room%20with%20rows%20of%20glowing%20equipment%20racks%2C%20blue%20and%20green%20LED%20indicator%20lights%20on%20hardware%2C%20an%20Asian%20male%20network%20engineer%20with%20short%20dark%20hair%20and%20no%20hat%20checking%20cable%20connections%20and%20server%20status%20in%20the%20foreground%2C%20wearing%20a%20clean%20professional%20short%20sleeve%20polo%20shirt%2C%20no%20headwear%20or%20cap%20visible%2C%20clean%20organized%20aisles%20with%20raised%20flooring%2C%20cool%20temperature%20indicated%20by%20subtle%20blue%20ambient%20lighting%2C%20authentic%20industrial%20technology%20environment%2C%20professional%20corporate%20photography%2C%20sharp%20detail%2C%20balanced%20exposure&width=800&height=600&seq=service-03-v3&orientation=landscape&nocache=true',
    },
  ];

  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!sliderRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const scrollToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="relative py-24 md:py-32 bg-background-100 overflow-hidden scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <div>
            <ScrollReveal delay={200}>
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-primary-800 leading-[1.12] tracking-tight">
                What We <span className="text-accent-500">Build</span>
              </h2>
            </ScrollReveal>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <span className="text-sm text-foreground-400">Drag</span>
            <div className="flex gap-2">
              <button
                onClick={scrollToPrev}
                className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
                aria-label="Previous slide"
              >
                <i className="ri-arrow-left-line text-foreground-600"></i>
              </button>
              <button
                onClick={scrollToNext}
                className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer"
                aria-label="Next slide"
              >
                <i className="ri-arrow-right-line text-foreground-600"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Draggable slider */}
      <div
        ref={sliderRef}
        className={`flex gap-6 overflow-x-auto px-6 md:px-10 lg:px-16 pb-4 scrollbar-hide ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {services.map((service) => (
          <ScrollReveal key={service.num} duration={450}>
            <div
              className="flex-shrink-0 w-[85vw] sm:w-[400px] md:w-[460px] group h-full"
              data-cursor="true"
              onMouseEnter={() => onCardHover?.(true)}
              onMouseLeave={() => onCardHover?.(false)}
            >
              <div className="bg-background-100 rounded-2xl overflow-hidden border border-background-200/70 hover:border-accent-500/15 transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                {/* Image */}
                <div className="relative w-full h-56 md:h-64 overflow-hidden flex-shrink-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    draggable={false}
                  />
                </div>

                {/* Content */}
                <div className="p-7 md:p-8 flex-1 flex flex-col">
                  <h3 className="font-heading font-semibold text-lg text-primary-800 mb-2">
                    {service.title}
                  </h3>

                  <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>
  );
}